import { motion } from "framer-motion";
import { springPresets } from "@/lib/motion";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { CONTACT } from "@/data/index";

export default function PoliticaCookies() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header simple */}
      <header className="sticky top-0 z-50 bg-primary shadow-md">
        <div className="container mx-auto px-4 py-3 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-primary-foreground/80 hover:text-accent transition-colors text-sm font-medium">
            <ArrowLeft size={18} /> Volver al inicio
          </Link>
          <img src="/images/logo.png" alt="Logo" className="h-10 w-auto object-contain ml-auto" />
        </div>
      </header>

      <div className="container mx-auto px-6 py-16 max-w-3xl">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={springPresets.gentle}>

          <div
            className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-6"
            style={{ background: "rgba(113,194,187,0.12)", color: "#71C2BB", border: "1px solid #71C2BB" }}
          >
            Privacidad
          </div>

          <h1 className="text-4xl font-extrabold mb-2" style={{ color: "#295F5A" }}>Política de Cookies</h1>
          <p className="text-muted-foreground text-sm mb-10">Última actualización: enero de 2025</p>

          <div className="space-y-8 text-foreground/80 leading-relaxed text-sm">

            <section>
              <h2 className="text-xl font-bold mb-3" style={{ color: "#295F5A" }}>1. ¿Qué son las cookies?</h2>
              <p>
                Las cookies son pequeños archivos de texto que los sitios web almacenan en el dispositivo del usuario cuando los visita. Permiten recordar información sobre la visita (idioma preferido, acceso, configuraciones, etc.), lo que puede facilitar la navegación y hacer que el sitio web resulte más útil para el usuario.
              </p>
            </section>

            <div className="h-px" style={{ background: "rgba(41,95,90,0.12)" }} />

            <section>
              <h2 className="text-xl font-bold mb-3" style={{ color: "#295F5A" }}>2. Tipos de cookies que utilizamos</h2>

              {/* Table */}
              <div className="rounded-xl overflow-hidden border mt-4" style={{ borderColor: "rgba(41,95,90,0.15)" }}>
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ background: "#295F5A", color: "#fff" }}>
                      <th className="text-left px-4 py-3 font-semibold">Nombre</th>
                      <th className="text-left px-4 py-3 font-semibold">Tipo</th>
                      <th className="text-left px-4 py-3 font-semibold">Duración</th>
                      <th className="text-left px-4 py-3 font-semibold">Finalidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { name: "cookie_consent", type: "Propia · Técnica", duration: "12 meses", purpose: "Almacena la preferencia de consentimiento del usuario sobre las cookies." },
                      { name: "_ga", type: "Analítica (Google Analytics)", duration: "2 años", purpose: "Distingue usuarios únicos; permite análisis de tráfico." },
                      { name: "_ga_*", type: "Analítica (Google Analytics)", duration: "2 años", purpose: "Mantiene el estado de la sesión de Analytics." },
                      { name: "_gid", type: "Analítica (Google Analytics)", duration: "24 horas", purpose: "Distingue usuarios para la sesión activa." },
                    ].map((row, i) => (
                      <tr key={row.name} style={{ background: i % 2 === 0 ? "#f9fafb" : "#fff" }}>
                        <td className="px-4 py-3 font-mono font-semibold" style={{ color: "#295F5A" }}>{row.name}</td>
                        <td className="px-4 py-3">{row.type}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{row.duration}</td>
                        <td className="px-4 py-3">{row.purpose}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <div className="h-px" style={{ background: "rgba(41,95,90,0.12)" }} />

            <section>
              <h2 className="text-xl font-bold mb-3" style={{ color: "#295F5A" }}>3. Cookies de terceros</h2>
              <p>
                Este sitio web puede utilizar Google Analytics, un servicio de analítica web prestado por Google LLC. Google Analytics utiliza cookies para ayudarnos a analizar el uso que hacen los usuarios del sitio. La información generada por la cookie acerca de su uso del sitio web será directamente transmitida y archivada por Google en los servidores de Estados Unidos.
              </p>
              <p className="mt-3">
                Puede consultar la política de privacidad de Google en:{" "}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: "#295F5A" }}>
                  policies.google.com/privacy
                </a>
              </p>
            </section>

            <div className="h-px" style={{ background: "rgba(41,95,90,0.12)" }} />

            <section>
              <h2 className="text-xl font-bold mb-3" style={{ color: "#295F5A" }}>4. Base jurídica del tratamiento</h2>
              <p>
                Las cookies técnicas o estrictamente necesarias no requieren consentimiento del usuario (art. 22.2 LSSI-CE). Las cookies analíticas y de terceros se instalan únicamente con el consentimiento expreso del usuario, que puede prestarse a través del banner de cookies mostrado en la primera visita al sitio.
              </p>
            </section>

            <div className="h-px" style={{ background: "rgba(41,95,90,0.12)" }} />

            <section>
              <h2 className="text-xl font-bold mb-3" style={{ color: "#295F5A" }}>5. Cómo gestionar o deshabilitar las cookies</h2>
              <p>Puedes gestionar, bloquear o eliminar las cookies a través de la configuración de tu navegador:</p>
              <ul className="mt-3 space-y-1.5 list-disc list-inside">
                <li><strong>Google Chrome:</strong> Configuración → Privacidad y seguridad → Cookies</li>
                <li><strong>Mozilla Firefox:</strong> Opciones → Privacidad y seguridad</li>
                <li><strong>Microsoft Edge:</strong> Configuración → Privacidad, búsqueda y servicios</li>
                <li><strong>Safari:</strong> Preferencias → Privacidad</li>
              </ul>
              <p className="mt-3">
                Ten en cuenta que deshabilitar las cookies puede afectar al correcto funcionamiento del sitio web.
              </p>
            </section>

            <div className="h-px" style={{ background: "rgba(41,95,90,0.12)" }} />

            <section>
              <h2 className="text-xl font-bold mb-3" style={{ color: "#295F5A" }}>6. Retirada del consentimiento</h2>
              <p>
                Puedes retirar tu consentimiento en cualquier momento eliminando la cookie <code className="px-1 py-0.5 rounded text-xs font-mono" style={{ background: "rgba(41,95,90,0.08)", color: "#295F5A" }}>cookie_consent</code> desde las opciones de tu navegador o borrando el almacenamiento local del sitio. La próxima visita volverá a mostrarte el banner de cookies.
              </p>
            </section>

            <div className="h-px" style={{ background: "rgba(41,95,90,0.12)" }} />

            <section>
              <h2 className="text-xl font-bold mb-3" style={{ color: "#295F5A" }}>7. Contacto</h2>
              <p>
                Para cualquier consulta relacionada con el uso de cookies o el tratamiento de datos, puedes dirigirte a:
              </p>
              <ul className="mt-3 space-y-1 list-none pl-0">
                <li><strong>Responsable:</strong> Oscar Alaminos Serrano</li>
                <li><strong>Dirección:</strong> {CONTACT.address}</li>
                <li><strong>Email:</strong> info@{CONTACT.web}</li>
              </ul>
            </section>

          </div>
        </motion.div>
      </div>

      <footer className="py-6 text-center text-xs mt-10" style={{ background: "#1e4a46", color: "rgba(255,255,255,0.4)" }}>
        <Link to="/" className="hover:text-accent transition-colors underline mr-4">Inicio</Link>
        <Link to="/aviso-legal" className="hover:text-accent transition-colors underline">Aviso Legal</Link>
        <p className="mt-2">© {new Date().getFullYear()} {CONTACT.name}</p>
      </footer>
    </div>
  );
}
